/**
 * mg-faq.js
 * Web Component: <mg-faq-accordion>
 *
 * Enhances native <details>/<summary> FAQ items with smooth height animation.
 * Falls back gracefully to native <details> behaviour when JS is unavailable.
 *
 * Follows Horizon theme Web Component conventions:
 *   - Extends HTMLElement
 *   - Defined via customElements.define()
 *   - Uses data attributes to communicate state to CSS
 *   - No framework dependencies
 *
 * Usage (in mg-faq.liquid):
 *   <mg-faq-accordion class="mg-faq__items">
 *     <!-- mg-faq-item blocks render <details class="mg-faq__item"> here -->
 *   </mg-faq-accordion>
 */

if (!customElements.get('mg-faq-accordion')) {
  class MgFaqAccordion extends HTMLElement {
    constructor() {
      super();
      this._onToggle = this._onToggle.bind(this);
    }

    connectedCallback() {
      // Signal to CSS that JS-controlled animation is active
      this.dataset.animated = '';

      // Initialise all items: close all answer panels to height 0
      this._items().forEach((details) => {
        const answer = details.querySelector('.mg-faq__answer');
        if (!answer) return;

        if (details.open) {
          // Already open — set to natural height so it doesn't snap shut
          answer.style.height = answer.scrollHeight + 'px';
        } else {
          answer.style.height = '0px';
        }
      });

      this.addEventListener('click', this._onToggle);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onToggle);
    }

    _items() {
      return Array.from(this.querySelectorAll('.mg-faq__item'));
    }

    _onToggle(event) {
      const summary = event.target.closest('summary');
      if (!summary) return;

      const details = summary.closest('.mg-faq__item');
      if (!details) return;

      // Prevent native toggle — we control it
      event.preventDefault();

      if (details.open) {
        this._close(details);
      } else {
        this._open(details);
      }
    }

    _open(details) {
      const answer = details.querySelector('.mg-faq__answer');
      if (!answer) return;

      // Mark open so chevron CSS rotates
      details.open = true;

      // Animate height from 0 to scrollHeight
      const targetHeight = answer.scrollHeight;
      answer.style.height = '0px';

      // Force reflow before animating
      answer.getBoundingClientRect();
      answer.style.height = targetHeight + 'px';

      const onEnd = () => {
        // Remove fixed height so content can reflow if viewport resizes
        answer.style.height = answer.scrollHeight + 'px';
        answer.removeEventListener('transitionend', onEnd);
      };
      answer.addEventListener('transitionend', onEnd, { once: true });
    }

    _close(details) {
      const answer = details.querySelector('.mg-faq__answer');
      if (!answer) return;

      // Animate height from current to 0
      answer.style.height = answer.scrollHeight + 'px';

      // Force reflow
      answer.getBoundingClientRect();
      answer.style.height = '0px';

      const onEnd = () => {
        details.open = false;
        answer.removeEventListener('transitionend', onEnd);
      };
      answer.addEventListener('transitionend', onEnd, { once: true });
    }
  }

  customElements.define('mg-faq-accordion', MgFaqAccordion);
}
